import type { DataFlowBuilder } from '@/engine/DataFlow';
import * as DT from './TAVRDataTypes';
import * as Algo from './TAVRAlgorithmHelpers';
import * as THREE from 'three';
import {
  buildAreaMeasurement,
  buildDeliveryAlgorithm,
  buildPointToPlaneDistanceMeasurementSigned,
  buildPointToPlaneDistanceMeasurementSignedInv
} from '../common/CardiacAlgorithmBuilderCommon';

export function buildAlgorithm(builder: DataFlowBuilder): void {
  builder.name('instrumentation.path').define(new DT.StringT('instr/tavr'));

  builder
    .name('instrumentation')
    .bind(Algo.loadInstrumentationLibraryAsync, DT.InstrumentationLibrary, 'instrumentation.path');

  builder.name('left_ventricle.descriptor').declare(DT.StringT);
  builder.name('aorta.descriptor').declare(DT.StringT);
  builder.name('aorta_lv.descriptor').declare(DT.StringT);

  builder.name('left_ventricle').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'left_ventricle.descriptor');
  builder.name('aorta').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'aorta.descriptor');
  builder.name('aorta_lv').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'aorta_lv.descriptor');

  builder.name('left_heart.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('rcc_calcification.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('lcc_calcification.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('ncc_calcification.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('additional_calcification.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('additional_device.descriptor').define(Algo.emptyMeshDescriptor());

  builder.name('left_heart').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'left_heart.descriptor');
  builder.name('rcc_calcification').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'rcc_calcification.descriptor');
  builder.name('lcc_calcification').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'lcc_calcification.descriptor');
  builder.name('ncc_calcification').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'ncc_calcification.descriptor');
  builder
    .name('additional_calcification')
    .bind(Algo.loadDracoMeshAsync, DT.Mesh, 'additional_calcification.descriptor');
  builder.name('additional_device').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'additional_device.descriptor');

  builder.name('aorta.triangles').bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'aorta');
  builder.name('aorta_lv.triangles').bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'aorta_lv');
  builder.name('left_ventricle.triangles').bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'left_ventricle');
  builder
    .name('rcc_calcification.triangles')
    .bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'rcc_calcification');
  builder
    .name('lcc_calcification.triangles')
    .bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'lcc_calcification');
  builder
    .name('ncc_calcification.triangles')
    .bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'ncc_calcification');

  builder.name('aorta.triangle_tree').bind(Algo.buildTriangleTree, DT.MatKDTree3d, 'aorta.triangles');

  builder.name('tp.pt_rcc.dflt').declare(DT.Point);
  builder.name('tp.pt_lcc.dflt').declare(DT.Point);
  builder.name('tp.pt_ncc.dflt').declare(DT.Point);
  builder.name('tp.pt_rc.dflt').declare(DT.Point);
  builder.name('tp.pt_lc.dflt').declare(DT.Point);
  builder.name('tp.pt_ms.dflt').declare(DT.Point);

  builder
    .name('tp.pt_rcc')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.existing('tp.pt_rcc.dflt'),
      builder.name('tp.pt_rcc.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_rcc.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_rcc.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_lcc')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.existing('tp.pt_lcc.dflt'),
      builder.name('tp.pt_lcc.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_lcc.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_lcc.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_ncc')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.existing('tp.pt_ncc.dflt'),
      builder.name('tp.pt_ncc.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_ncc.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_ncc.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_rc')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.existing('tp.pt_rc.dflt'),
      builder.name('tp.pt_rc.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_rc.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_rc.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_lc')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.existing('tp.pt_lc.dflt'),
      builder.name('tp.pt_lc.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_lc.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_lc.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_ms')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.existing('tp.pt_ms.dflt'),
      builder.name('tp.pt_ms.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_ms.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_ms.flag').define(Algo.defaultTuneFlag())
    );

  builder.name('pt_mvc_imported').declare(DT.Point);
  builder.name('pt_sov_rcc_1_imported').declare(DT.Point);
  builder.name('pt_sov_rcc_2_imported').declare(DT.Point);
  builder.name('pt_sov_lcc_1_imported').declare(DT.Point);
  builder.name('pt_sov_lcc_2_imported').declare(DT.Point);
  builder.name('pt_sov_ncc_1_imported').declare(DT.Point);
  builder.name('pt_sov_ncc_2_imported').declare(DT.Point);
  builder.name('pt_stj_1_imported').declare(DT.Point);
  builder.name('pt_stj_2_imported').declare(DT.Point);
  builder.name('pt_stj_3_imported').declare(DT.Point);
  builder.name('pt_aa_1_imported').declare(DT.Point);
  builder.name('pt_aa_2_imported').declare(DT.Point);
  builder.name('pt_aa_3_imported').declare(DT.Point);

  builder.name('world_top_direction').define(new DT.Vector(new THREE.Vector3(0, 0, 1)));
  builder.name('world_bottom_direction').define(new DT.Vector(new THREE.Vector3(0, 0, -1)));
  builder.name('world_posterior_direction').define(new DT.Vector(new THREE.Vector3(0, 1, 0)));
  builder.name('world_anterior_direction').define(new DT.Vector(new THREE.Vector3(0, -1, 0)));
  builder.name('world_left_direction').define(new DT.Vector(new THREE.Vector3(1, 0, 0)));
  builder.name('world_right_direction').define(new DT.Vector(new THREE.Vector3(-1, 0, 0)));

  builder
    .name('body_orientation')
    .bind(Algo.makeOrientationTop, DT.Orientation, 'world_top_direction', 'world_posterior_direction');

  builder.name('left_direction').bind(Algo.unitVectorFromPoints, DT.Vector, 'tp.pt_rc.dflt', 'tp.pt_lc.dflt');
  builder.name('right_direction').bind(Algo.unitVectorFromPoints, DT.Vector, 'tp.pt_lc.dflt', 'tp.pt_rc.dflt');

  builder
    .name('pl_ascending_aorta')
    .bind(
      Algo.planeFromThreePoints,
      DT.Plane,
      'pt_aa_1_imported',
      'pt_aa_2_imported',
      'pt_aa_3_imported',
      'world_top_direction'
    );
  builder
    .name('pl_stj')
    .bind(
      Algo.planeFromThreePoints,
      DT.Plane,
      'pt_stj_1_imported',
      'pt_stj_2_imported',
      'pt_stj_3_imported',
      'world_top_direction'
    );
  builder
    .name('pl_sov')
    .bind(
      Algo.planeFromThreePoints,
      DT.Plane,
      'pt_sov_rcc_1_imported',
      'pt_sov_rcc_2_imported',
      'pt_sov_ncc_1_imported',
      'world_top_direction'
    );
  builder
    .name('pl_aortic_annulus_dflt')
    .bind(
      Algo.planeFromThreePoints,
      DT.Plane,
      'tp.pt_rcc.dflt',
      'tp.pt_lcc.dflt',
      'tp.pt_ncc.dflt',
      'world_top_direction'
    );

  builder
    .name('aorta_lv.centerline.points')
    .bind(
      Algo.makeAortaLvCenterlineTAVR,
      DT.Polyline,
      'aorta.triangles',
      'left_ventricle.triangles',
      'pl_aortic_annulus_dflt',
      'pl_stj',
      'pl_ascending_aorta'
    );

  builder
    .name('pl_aortic_annulus')
    .bind(Algo.planeFromThreePoints, DT.Plane, 'tp.pt_rcc', 'tp.pt_lcc', 'tp.pt_ncc', 'world_top_direction');

  builder
    .name('tp.pl_lvot')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder
        .name('tp.pl_lvot.dflt')
        .bindRaw(
          Algo.translatePlane,
          DT.Plane,
          builder.existing('pl_aortic_annulus'),
          builder.define(new DT.NumberT(-4))
        ),
      builder.name('tp.pl_lvot.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_lvot.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_lvot.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pl_ascending_aorta')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder.name('tp.pl_ascending_aorta.dflt').bind(Algo.reference, DT.Plane, 'pl_ascending_aorta'),
      builder.name('tp.pl_ascending_aorta.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_ascending_aorta.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_ascending_aorta.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pl_stj')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder.name('tp.pl_stj.dflt').bind(Algo.reference, DT.Plane, 'pl_stj'),
      builder.name('tp.pl_stj.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_stj.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_stj.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pl_sov')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder.name('tp.pl_sov.dflt').bind(Algo.adjustPlaneCL, DT.Plane, 'pl_sov', 'aorta_lv.centerline.points'),
      builder.name('tp.pl_sov.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_sov.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_sov.flag').define(Algo.defaultTuneFlag())
    );

  builder.name('aorta_lv.centerline').bind(
    Algo.makeCenterline,
    DT.Centerline,
    'aorta_lv.centerline.points',
    'tp.pl_ascending_aorta.dflt',
    'tp.pl_stj.dflt',
    'tp.pl_sov.dflt',
    'pl_aortic_annulus',
    'tp.pl_lvot.dflt'
    //'tp.pl_ascending_aorta',
    //'tp.pl_stj',
    //'tp.pl_sov',
    //'pl_aortic_annulus',
    //'tp.pl_lvot'
  );

  builder
    .name('tp.pt_sov_rcc_1')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_sov_rcc_1.dflt')
        .bind(
          Algo.adjustSovPointTAVR,
          DT.Point,
          'aorta.triangles',
          'aorta.triangle_tree',
          'tp.pl_sov',
          'pt_sov_rcc_1_imported',
          'pt_sov_rcc_2_imported'
        ),
      builder.name('tp.pt_sov_rcc_1.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_rcc_1.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_rcc_1.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_sov_rcc_2')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_sov_rcc_2.dflt')
        .bind(
          Algo.adjustSovPointTAVR,
          DT.Point,
          'aorta.triangles',
          'aorta.triangle_tree',
          'tp.pl_sov',
          'pt_sov_rcc_2_imported',
          'pt_sov_rcc_1_imported'
        ),
      builder.name('tp.pt_sov_rcc_2.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_rcc_2.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_rcc_2.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_sov_lcc_1')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_sov_lcc_1.dflt')
        .bind(
          Algo.adjustSovPointTAVR,
          DT.Point,
          'aorta.triangles',
          'aorta.triangle_tree',
          'tp.pl_sov',
          'pt_sov_lcc_1_imported',
          'pt_sov_lcc_2_imported'
        ),
      builder.name('tp.pt_sov_lcc_1.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_lcc_1.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_lcc_1.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_sov_lcc_2')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_sov_lcc_2.dflt')
        .bind(
          Algo.adjustSovPointTAVR,
          DT.Point,
          'aorta.triangles',
          'aorta.triangle_tree',
          'tp.pl_sov',
          'pt_sov_lcc_2_imported',
          'pt_sov_lcc_1_imported'
        ),
      builder.name('tp.pt_sov_lcc_2.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_lcc_2.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_lcc_2.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_sov_ncc_1')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_sov_ncc_1.dflt')
        .bind(
          Algo.adjustSovPointTAVR,
          DT.Point,
          'aorta.triangles',
          'aorta.triangle_tree',
          'tp.pl_sov',
          'pt_sov_ncc_1_imported',
          'pt_sov_ncc_2_imported'
        ),
      builder.name('tp.pt_sov_ncc_1.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_ncc_1.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_ncc_1.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_sov_ncc_2')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_sov_ncc_2.dflt')
        .bind(
          Algo.adjustSovPointTAVR,
          DT.Point,
          'aorta.triangles',
          'aorta.triangle_tree',
          'tp.pl_sov',
          'pt_sov_ncc_2_imported',
          'pt_sov_ncc_1_imported'
        ),
      builder.name('tp.pt_sov_ncc_2.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_ncc_2.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_sov_ncc_2.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('coronary_posterior_direction')
    .bind(Algo.coronaryPosteriorDirectionTAVR, DT.Vector, 'pl_aortic_annulus', 'tp.pt_lc', 'tp.pt_rc');
  builder.name('coronary_top_direction').bind(Algo.normalOf, DT.Vector, 'pl_aortic_annulus');
  builder
    .name('coronary_left_direction')
    .bind(Algo.crossProduct, DT.Vector, 'coronary_posterior_direction', 'coronary_top_direction');
  builder.name('coronary_anterior_direction').bind(Algo.invertVector, DT.Vector, 'coronary_posterior_direction');
  builder.name('coronary_bottom_direction').bind(Algo.invertVector, DT.Vector, 'coronary_top_direction');
  builder.name('coronary_right_direction').bind(Algo.invertVector, DT.Vector, 'coronary_left_direction');

  builder
    .name('ln_sov_lcc')
    .bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_sov_lcc_1', 'tp.pt_sov_lcc_2', 'coronary_right_direction');
  builder
    .name('ln_sov_rcc')
    .bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_sov_rcc_1', 'tp.pt_sov_rcc_2', 'coronary_left_direction');
  builder
    .name('ln_sov_ncc')
    .bind(Algo.lineBetweenPoints, DT.Line, 'tp.pt_sov_ncc_1', 'tp.pt_sov_ncc_2', 'coronary_anterior_direction');

  builder
    .name('aorta_orientation')
    .bind(Algo.makeOrientationTop, DT.Orientation, 'coronary_top_direction', 'coronary_posterior_direction');

  builder
    .name('heart_orientation')
    .bind(Algo.heartOrientationTAVR, DT.Orientation, 'tp.pt_rcc', 'tp.pt_lcc', 'tp.pt_ncc', 'world_top_direction');

  builder
    .name('cusp_overlap_orientation')
    .bind(Algo.cuspOverlapOrientationTAVR, DT.Orientation, 'tp.pt_rcc', 'tp.pt_lcc', 'world_top_direction');

  buildAreaMeasurement(builder, 'tp.pl_stj', 'aorta.triangles', 'world_top_direction');
  buildAreaMeasurement(builder, 'tp.pl_ascending_aorta', 'aorta.triangles', 'world_top_direction');
  buildAreaMeasurement(
    builder,
    'pl_aortic_annulus',
    'left_ventricle.triangles',
    'world_top_direction',
    undefined,
    'tp.pt_ncc'
  );
  buildAreaMeasurement(builder, 'tp.pl_lvot', 'aorta_lv.triangles', 'world_top_direction', undefined, 'tp.pt_ncc');

  builder
    .name('var_aortic_angulation')
    .bind(Algo.calculateAorticAngulationTAVR, DT.AngleMeasurement, 'pl_aortic_annulus.area');

  builder.name('device_roi').bind(Algo.getMeasurementROI, DT.RegionOfInterest, 'pl_aortic_annulus.area');

  const lcHeightInfo = buildPointToPlaneDistanceMeasurementSigned(builder, 'tp.pt_lc', 'pl_aortic_annulus');

  builder
    .name('var_left_coronary_height')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      lcHeightInfo.distance,
      lcHeightInfo.line,
      builder.bindRaw(
        Algo.makeRoiForVerticalLine,
        DT.RegionOfInterest,
        lcHeightInfo.line,
        builder.existing('coronary_anterior_direction'),
        builder.existing('world_top_direction')
      )
    );

  const rcHeightInfo = buildPointToPlaneDistanceMeasurementSigned(builder, 'tp.pt_rc', 'pl_aortic_annulus');

  builder
    .name('var_right_coronary_height')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      rcHeightInfo.distance,
      rcHeightInfo.line,
      builder.bindRaw(
        Algo.makeRoiForVerticalLine,
        DT.RegionOfInterest,
        rcHeightInfo.line,
        builder.existing('coronary_anterior_direction'),
        builder.existing('world_top_direction')
      )
    );

  const msLengthInfo = buildPointToPlaneDistanceMeasurementSignedInv(builder, 'tp.pt_ms', 'pl_aortic_annulus');

  builder
    .name('var_membranous_septum_length')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      msLengthInfo.distance,
      msLengthInfo.line,
      builder.bindRaw(
        Algo.makeRoiForVerticalLine,
        DT.RegionOfInterest,
        msLengthInfo.line,
        builder.existing('coronary_anterior_direction'),
        builder.existing('world_top_direction')
      )
    );

  builder
    .name('sov_roi')
    .bind(Algo.sovRoiTAVR, DT.RegionOfInterest, 'tp.pl_sov', 'ln_sov_lcc', 'ln_sov_rcc', 'ln_sov_ncc');

  builder
    .name('var_lcc')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_sov_lcc'),
      builder.existing('ln_sov_lcc'),
      builder.existing('sov_roi')
    );
  builder
    .name('var_rcc')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_sov_rcc'),
      builder.existing('ln_sov_rcc'),
      builder.existing('sov_roi')
    );
  builder
    .name('var_ncc')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLine, DT.NumberT, 'ln_sov_ncc'),
      builder.existing('ln_sov_ncc'),
      builder.existing('sov_roi')
    );

  builder.name('var_lcc_volume').bind(Algo.volumeOfTheMesh, DT.NumberT, 'lcc_calcification.triangles');
  builder.name('var_rcc_volume').bind(Algo.volumeOfTheMesh, DT.NumberT, 'rcc_calcification.triangles');
  builder.name('var_ncc_volume').bind(Algo.volumeOfTheMesh, DT.NumberT, 'ncc_calcification.triangles');
  builder.name('var_ccc_volume').bind(Algo.sumUp, DT.NumberT, 'var_lcc_volume', 'var_rcc_volume', 'var_ncc_volume');

  builder
    .name('tp.manufacturer')
    .bindRaw(
      Algo.tuneValue,
      DT.StringT,
      builder.name('tp.manufacturer.dflt').bind(Algo.getPreferredManufacturer, DT.StringT, 'instrumentation'),
      builder.name('tp.manufacturer.user').define(new DT.StringT('')),
      builder.name('tp.manufacturer.appr').define(new DT.StringT('')),
      builder.name('tp.manufacturer.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.manufacturer.list').bind(Algo.getAvailableManufacturers, DT.IdentifiersList, 'instrumentation');

  builder
    .name('instrumentation.manufacturer')
    .bind(Algo.getCardiacManufacturer, DT.CardiacManufacturer, 'instrumentation', 'tp.manufacturer');

  builder
    .name('tp.system')
    .bindRaw(
      Algo.tuneValue,
      DT.StringT,
      builder.name('tp.system.dflt').bind(Algo.getPreferredSystem, DT.StringT, 'instrumentation.manufacturer'),
      builder.name('tp.system.user').define(new DT.StringT('')),
      builder.name('tp.system.appr').define(new DT.StringT('')),
      builder.name('tp.system.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.system.list').bind(Algo.getAvailableSystems, DT.IdentifiersList, 'instrumentation.manufacturer');

  builder
    .name('instrumentation.system')
    .bind(Algo.getSystem, DT.CardiacSystem, 'instrumentation.manufacturer', 'tp.system');

  builder
    .name('tp.valveInsertionDepth')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder
        .name('tp.valveInsertionDepth.dflt')
        .bind(Algo.getValveInsertionDepth, DT.NumberT, 'instrumentation.system'),
      builder.name('tp.valveInsertionDepth.user').define(new DT.NumberT(0)),
      builder.name('tp.valveInsertionDepth.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveInsertionDepth.flag').define(Algo.defaultTuneFlag())
    );
  builder
    .name('tp.valveInsertionDepth.range')
    .bind(Algo.getValveInsertionDepthRange, DT.RangeT, 'instrumentation.system');

  builder
    .name('tp.valveApRotationDeg')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.valveApRotationDeg.dflt').define(new DT.NumberT(0)),
      builder.name('tp.valveApRotationDeg.user').define(new DT.NumberT(0)),
      builder.name('tp.valveApRotationDeg.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveApRotationDeg.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valveApRotationDeg.range').define(new DT.RangeT([-10, 10]));

  builder
    .name('tp.valveLrRotationDeg')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.valveLrRotationDeg.dflt').define(new DT.NumberT(0)),
      builder.name('tp.valveLrRotationDeg.user').define(new DT.NumberT(0)),
      builder.name('tp.valveLrRotationDeg.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveLrRotationDeg.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valveLrRotationDeg.range').define(new DT.RangeT([-10, 10]));

  builder
    .name('tp.valveRotationAngleDeg')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.valveRotationAngleDeg.dflt').define(new DT.NumberT(0)),
      builder.name('tp.valveRotationAngleDeg.user').define(new DT.NumberT(0)),
      builder.name('tp.valveRotationAngleDeg.appr').define(new DT.NumberT(0)),
      builder.name('tp.valveRotationAngleDeg.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valveRotationAngleDeg.range').define(new DT.RangeT([-180, 180]));

  builder
    .name('tp.valve')
    .bindRaw(
      Algo.tuneValue,
      DT.StringT,
      builder
        .name('tp.valve.dflt')
        .bind(Algo.defaultValveSizeTAVR, DT.StringT, 'instrumentation.system', 'pl_aortic_annulus.area'),
      builder.name('tp.valve.user').define(new DT.StringT('')),
      builder.name('tp.valve.appr').define(new DT.StringT('')),
      builder.name('tp.valve.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.valve.list').bind(Algo.getAvailableValveDevicesTAVR, DT.IdentifiersList, 'instrumentation.system');

  builder.name('device.valve').bind(Algo.getValveDeviceTAVR, DT.TAVRValveDevice, 'instrumentation.system', 'tp.valve');

  builder.name('valve').bind(Algo.getDeviceModelAsync, DT.Mesh, 'device.valve');

  builder
    .name('valve.cs')
    .bind(
      Algo.defaultValvePositionTAVR,
      DT.TransformMatrix,
      'tp.valveInsertionDepth',
      'tp.valveApRotationDeg',
      'tp.valveLrRotationDeg',
      'tp.valveRotationAngleDeg',
      'pl_aortic_annulus.area'
    );

  builder
    .name('tp.valve_morphing_enabled')
    .bindRaw(
      Algo.tuneValue,
      DT.BooleanT,
      builder.name('tp.valve_morphing_enabled.dflt').define(new DT.BooleanT(false)),
      builder.name('tp.valve_morphing_enabled.user').define(new DT.BooleanT(false)),
      builder.name('tp.valve_morphing_enabled.appr').define(new DT.BooleanT(false)),
      builder.name('tp.valve_morphing_enabled.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('valve_dynamic')
    .bind(
      Algo.generateValveDynamicPartTAVR,
      DT.Mesh,
      'device.valve',
      'valve.cs',
      'aorta.triangle_tree',
      'aorta.triangles',
      'tp.valve_morphing_enabled'
    );

  builder.name('valve_dynamic.cs').bind(Algo.cloneTransformMatrix, DT.TransformMatrix, 'valve.cs');

  builder.name('pl_valve_bottom').bind(Algo.valveBottomPlaneTAVR, DT.Plane, 'valve.cs');

  builder.name('pl_valve_risk').bind(Algo.valveRiskPlaneTAVR, DT.Plane, 'device.valve', 'valve.cs');

  const msDistanceInfo = buildPointToPlaneDistanceMeasurementSignedInv(builder, 'tp.pt_ms', 'pl_valve_bottom');

  builder
    .name('var_membranous_septum_device_distance')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      msDistanceInfo.distance,
      msDistanceInfo.line,
      builder.bindRaw(
        Algo.makeRoiForVerticalLine,
        DT.RegionOfInterest,
        msDistanceInfo.line,
        builder.existing('coronary_anterior_direction'),
        builder.existing('world_top_direction')
      )
    );

  const lcDistanceInfo = buildPointToPlaneDistanceMeasurementSigned(builder, 'tp.pt_lc', 'pl_valve_risk');

  builder
    .name('var_left_coronary_skirt_distance')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      lcDistanceInfo.distance,
      lcDistanceInfo.line,
      builder.bindRaw(
        Algo.makeRoiForVerticalLine,
        DT.RegionOfInterest,
        lcDistanceInfo.line,
        builder.existing('coronary_anterior_direction'),
        builder.existing('world_top_direction')
      )
    );

  const rcDistanceInfo = buildPointToPlaneDistanceMeasurementSigned(builder, 'tp.pt_rc', 'pl_valve_risk');

  builder
    .name('var_right_coronary_skirt_distance')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      rcDistanceInfo.distance,
      rcDistanceInfo.line,
      builder.bindRaw(
        Algo.makeRoiForVerticalLine,
        DT.RegionOfInterest,
        rcDistanceInfo.line,
        builder.existing('coronary_anterior_direction'),
        builder.existing('world_top_direction')
      )
    );

  builder.name('ln_vtc_lca').bind(Algo.calculateVTC_TAVR, DT.Line, 'device.valve', 'valve.cs', 'tp.pt_lc');

  builder.name('ln_vtc_rca').bind(Algo.calculateVTC_TAVR, DT.Line, 'device.valve', 'valve.cs', 'tp.pt_rc');

  builder.name('pl_valve_lc').bind(Algo.valveAxialPlaneTAVR, DT.Plane, 'valve.cs', 'tp.pt_lc');
  builder.name('pl_valve_rc').bind(Algo.valveAxialPlaneTAVR, DT.Plane, 'valve.cs', 'tp.pt_rc');

  builder
    .name('pts_vta_lca')
    .bind(Algo.coronaryRiskPointsTAVR, DT.Polyline, 'aorta.triangles', 'pl_valve_lc', 'tp.pl_stj');

  builder
    .name('pts_vta_rca')
    .bind(Algo.coronaryRiskPointsTAVR, DT.Polyline, 'aorta.triangles', 'pl_valve_rc', 'tp.pl_stj');

  builder.name('ln_vta_lca').bind(Algo.calculateVTA_TAVR, DT.Line, 'device.valve', 'valve.cs', 'pts_vta_lca');

  builder.name('ln_vta_rca').bind(Algo.calculateVTA_TAVR, DT.Line, 'device.valve', 'valve.cs', 'pts_vta_rca');

  builder
    .name('lca_roi')
    .bind(Algo.vtaVtcRoiTAVR, DT.RegionOfInterest, 'valve.cs', 'tp.pt_lc', 'coronary_anterior_direction');
  builder
    .name('rca_roi')
    .bind(Algo.vtaVtcRoiTAVR, DT.RegionOfInterest, 'valve.cs', 'tp.pt_rc', 'coronary_anterior_direction');

  builder
    .name('var_vtc_lca')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLineSigned, DT.NumberT, 'ln_vtc_lca', 'right_direction'),
      builder.existing('ln_vtc_lca'),
      builder.existing('lca_roi')
    );

  builder
    .name('var_vtc_rca')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLineSigned, DT.NumberT, 'ln_vtc_rca', 'left_direction'),
      builder.existing('ln_vtc_rca'),
      builder.existing('lca_roi')
    );

  builder
    .name('var_vta_lca')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLineSigned, DT.NumberT, 'ln_vta_lca', 'right_direction'),
      builder.existing('ln_vta_lca'),
      builder.existing('lca_roi')
    );

  builder
    .name('var_vta_rca')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      builder.bind(Algo.lengthOfTheLineSigned, DT.NumberT, 'ln_vta_rca', 'left_direction'),
      builder.existing('ln_vta_rca'),
      builder.existing('lca_roi')
    );

  // Delivery ------------------------------------------------------------------------------

  builder.name('heart.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('aneurysm.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('rib_cage.descriptor').define(Algo.emptyMeshDescriptor());

  builder.name('heart').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'heart.descriptor');
  builder.name('aneurysm').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'aneurysm.descriptor');
  builder.name('rib_cage').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'rib_cage.descriptor');

  buildDeliveryAlgorithm(builder);
}
